/**
 * Overview builder state (§2.11, screenshots/67–74, 90).
 *
 * - `overviewLayout`: the grid itself, a `createLayoutStore` (grid/layoutStore.ts): layout, customize mode, keyboard
 *   pick-up, pointer drag preview, 50-step history and the 600 ms autosave. Saves carry `baseVersion` + the widgets I
 *   touched; a 409 comes back with their layout and the store merges per widget (mergeLayouts) and re-saves (90).
 * - `overviewStore`: the page around it: snapshot, My / Team layout, feeds, dataset rows, the widget being removed
 *   (widgetRemove + 5 s Undo) and the widgets added this session (their charts run chartEnter, never on first paint).
 *
 * Hydrated from `snapshot:overview` before the first render (main.tsx), so the first frame is the saved layout.
 * A member's own edits to the team layout fork into My layout (74 #10).
 */
import { createStore } from "zustand/vanilla";
import { subscribeWithSelector } from "zustand/middleware";
import { useStore } from "zustand";
import { recipes } from "../motion";
import type { UserGroup } from "../nav/navConfig";
import { toastStore } from "../stores/toastStore";
import { bindingToQuery } from "./charts/chartSettings";
import { entryOf } from "./catalog";
import { groupDefault } from "./defaults";
import { sanitize } from "./grid/layoutEngine";
import { createLayoutStore, useLayout, type LayoutState, type SaveResult } from "./grid/layoutStore";
import type { DatasetResult, LayoutItem, LayoutScope, OverviewSnapshot, SavedLayout, WidgetFeeds, WidgetType } from "./types";
import { WIDGET_TYPES, widgetType } from "./widgets/registry";
import { withPageFilters, type PageFilters } from "../analytics/pageFilters";

export type SaveState = LayoutState["status"];

/** Saved layouts may come from before the `widgetId` → `id` rename or use the starter's widget names. */
export function normalizeItems(items: unknown[] | null | undefined): LayoutItem[] {
  const out: LayoutItem[] = [];
  for (const raw of items ?? []) {
    const i = raw as Partial<LayoutItem> & { widgetId?: string };
    const type = typeof i?.type === "string" ? widgetType(i.type) : null;
    const id = i?.id ?? i?.widgetId;
    if (!type || typeof id !== "string") continue;
    out.push({ id, type, x: Number(i.x) || 0, y: Number(i.y) || 0, w: Number(i.w) || 3, h: Number(i.h) || 2, config: (i.config ?? {}) as LayoutItem["config"] });
  }
  return sanitize(out, WIDGET_TYPES);
}

async function putLayout(scope: LayoutScope, items: LayoutItem[], baseVersion: number, touched: string[]): Promise<SaveResult<LayoutItem>> {
  const r = await fetch("/api/me/overview", { method: "PUT", credentials: "include", headers: { "content-type": "application/json" },
    body: JSON.stringify({ scope, items, baseVersion, touched }) });
  if (r.status === 409) {
    const c = await r.json() as { items: unknown[]; version: number; touched: string[]; by: string };
    return { ok: false, conflict: { layout: normalizeItems(c.items), version: c.version, touched: c.touched ?? [], by: c.by } };
  }
  if (!r.ok) throw new Error(String(r.status));
  const d = await r.json().catch(() => ({})) as { version?: number };
  return { ok: true, version: d.version ?? 0 };
}

/**
 * Analytics (screenshots/84): a dashboard is painted by this same grid and these same widgets. Its page sets where
 * layout saves go (`/api/dashboards/<id>` with the version it started from) instead of the Overview's layouts.
 */
type LayoutSaver = (layout: LayoutItem[], baseVersion: number, touched: string[]) => Promise<SaveResult<LayoutItem>>;
let layoutSaver: LayoutSaver | null = null;
export const setLayoutSaver = (fn: LayoutSaver | null) => { layoutSaver = fn; };

/** The Overview grid. `save` decides the scope at save time: members' edits to the team layout fork into My layout. */
export const overviewLayout = createLayoutStore<LayoutItem>([], 0, {
  save: async (layout, baseVersion, touched) => {
    if (layoutSaver) return layoutSaver(layout, baseVersion, touched);
    const o = overviewStore.getState();
    let scope = o.scope;
    if (scope === "team" && !o.snapshot?.viewer.canPublish) {
      scope = "mine";
      overviewStore.setState({ scope });
      baseVersion = o.snapshot?.layouts.mine?.version ?? 0;
    }
    const r = await putLayout(scope, layout, baseVersion, touched);
    // Keep the snapshot's copy current, so switching My / Team layout starts from what was saved.
    const snap = overviewStore.getState().snapshot;
    if (r.ok && snap) overviewStore.setState({ snapshot: { ...snap, layouts: { ...snap.layouts,
      [scope]: { ...(snap.layouts[scope] ?? { updatedAt: null }), items: layout, version: r.version, updatedAt: new Date().toISOString() } } } });
    return r;
  },
});
export const useOverviewLayout = <R,>(sel: (s: LayoutState<LayoutItem>) => R) => useLayout(overviewLayout, sel);
const L = () => overviewLayout.getState();

export interface OverviewState {
  snapshot: OverviewSnapshot | null;
  group: UserGroup;
  scope: LayoutScope;
  /** The selected widget in customize mode (handles, drag pill, size tag). */
  selected: string | null;
  /** Widgets added this session: their charts run chartEnter (never on first paint). */
  justAdded: string[];
  /** The widget playing widgetRemove (shrink + fade) before it leaves the layout. */
  removing: string | null;
  datasets: Record<string, DatasetResult>;
  feeds: WidgetFeeds;
  /** Someone else editing this overview right now (90; org channel presence): their name and the widgets they touched. */
  presence: { name: string; touched: string[] } | null;
  /** What the grid shows: "overview", "dashboard:<id>" or "link:<token>" (Analytics reuses this store). */
  host: string;
  /** Dashboard filters (84: Since · Topic · Wards), applied to every data widget's query; {} on the Overview. */
  pageFilters: PageFilters;
  /** View-only dashboards and public links: no Edit chart / Duplicate / Remove in the widget menu. */
  readOnly: boolean;

  /** `opts.exact`: show the saved items even when empty (a blank dashboard isn't filled with the group defaults). */
  hydrate: (s: OverviewSnapshot, group: UserGroup, opts?: { host?: string; exact?: boolean; filters?: PageFilters; readOnly?: boolean }) => void;
  setPageFilters: (f: PageFilters) => void;
  setScope: (scope: LayoutScope) => void;
  select: (id: string | null) => void;
  addWidget: (type: WidgetType, at?: { x: number; y: number }) => string;
  removeWidget: (id: string) => void;
  setConfig: (id: string, patch: Partial<LayoutItem["config"]>) => void;
  reset: () => void;
  loadDataset: (item: LayoutItem) => void;
}

const savedItems = (saved: SavedLayout | null | undefined, group: UserGroup) => {
  const items = normalizeItems(saved?.items);
  return items.length ? items : groupDefault(group);
};

export const overviewStore = createStore<OverviewState>()(subscribeWithSelector((set, get) => ({
  snapshot: null, group: "advocacy", scope: "mine", selected: null, justAdded: [], removing: null, datasets: {}, feeds: {}, presence: null, host: "overview", pageFilters: {}, readOnly: false,

  hydrate: (s, group, opts) => {
    const scope = s.layouts.active;
    const saved = scope === "team" ? s.layouts.team : s.layouts.mine;
    set({ snapshot: s, group, scope, datasets: s.datasets, feeds: s.feeds, host: opts?.host ?? "overview", pageFilters: opts?.filters ?? {}, readOnly: !!opts?.readOnly, presence: null, selected: null, justAdded: [] });
    L().hydrate(opts?.exact ? normalizeItems(saved?.items) : savedItems(saved, group), saved?.version ?? 0);
    overviewLayout.setState({ status: saved ? "saved" : "idle" });
  },

  setScope: (scope) => {
    const s = get().snapshot;
    if (!s || scope === get().scope) return;
    const saved = scope === "team" ? s.layouts.team : s.layouts.mine;
    set({ scope, selected: null });
    L().hydrate(savedItems(saved, get().group), saved?.version ?? 0);
    fetch("/api/me/overview", { method: "PUT", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ active: scope }) }).catch(() => {});
    for (const it of L().layout) get().loadDataset(it);
  },

  select: (id) => set({ selected: id }),

  // Data widgets re-query on their own when their query changes (DataWidget → useDatasetQuery).
  setPageFilters: (pageFilters) => set({ pageFilters }),

  addWidget: (type, at) => {
    const id = L().add(type, at);
    set({ justAdded: [...get().justAdded, id], selected: id });
    return id;
  },

  /** O5: shrink + fade (160ms), then the gap closes (FLIP) and a 5s Undo toast restores the exact cells. */
  removeWidget: (id) => {
    const before = L().layout;
    const item = before.find((i) => i.id === id);
    if (!item) return;
    set({ removing: id });
    setTimeout(() => {
      set({ removing: null, selected: get().selected === id ? null : get().selected });
      L().remove(id);
      toastStore.getState().show({ message: `Removed ${item.config.title ?? entryOf(item.type).title}`, actionLabel: "Undo",
        onAction: () => L().apply(() => before, [id], `Restored ${entryOf(item.type).title}`) });
    }, recipes.widgetRemove.duration);
  },

  setConfig: (id, patch) => {
    const cur = L().layout.find((i) => i.id === id);
    if (!cur) return;
    L().setConfig(id, { ...cur.config, ...patch });
    const it = L().layout.find((i) => i.id === id);
    if (it) get().loadDataset(it);
  },

  /** Reset goes to the team layout, or to the group default if there isn't one (§2.11 Layouts). */
  reset: () => {
    const team = normalizeItems(get().snapshot?.layouts.team?.items);
    const next = structuredClone(team.length ? team : groupDefault(get().group));
    L().apply(() => next, next.map((i) => i.id), "Layout reset");
  },

  /** Rows for a chart/table widget that wasn't in the snapshot (added later, or the sources toggle changed). */
  loadDataset: (item) => {
    const data = item.config.settings?.data && withPageFilters(item.config.settings.data, get().pageFilters);
    if (!data) return;
    const binding = item.config.sources === "company" ? { ...data, filters: [...data.filters, { field: "contributorType", op: "is" as const, value: "company" }] } : data;
    const url = bindingToQuery(binding);
    if (get().datasets[item.id]?.query === url) return;
    fetch(url, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).then((d: DatasetResult | null) => {
      if (d) set({ datasets: { ...get().datasets, [item.id]: d } });
    }).catch(() => {});
  },
})));

export const useOverview = <T,>(selector: (s: OverviewState) => T) => useStore(overviewStore, selector);
